import { buildDateFromISOString } from '../../utils/time';
import { comparisonColors } from '../../utils/colors';
import {
    displayCurrency,
    displayShortDate,
    displayShortDateShortYear
} from '../../utils/Format/Format';

const calculateCanShow = (inceptionDate, growthAmounts) => {
    if (!(growthAmounts && growthAmounts.length)) {
        return false;
    }
    if (!inceptionDate) return false;
    const lastDateIsoString =
        growthAmounts[growthAmounts.length - 1].asOfDate || null;
    if (!lastDateIsoString) {
        return false;
    }
    const lastDateObj = buildDateFromISOString(lastDateIsoString);
    const oneYearDateObj = buildDateFromISOString(inceptionDate);
    // Example: if inception was 3/16/2024 find 3/30/2025
    // move one year in the future
    oneYearDateObj.setFullYear(oneYearDateObj.getFullYear() + 1);
    // find date second to last day of the month
    oneYearDateObj.setMonth(lastDateObj.getMonth() + 1);
    oneYearDateObj.setDate(-1);

    return lastDateObj > oneYearDateObj;
};

export const removeElementsWithDuplicateDates = (arr) =>
    arr.filter((ele, index, origArr) => {
        const { asOfDate: dateToMatch } = ele;
        const firstMatchIndex = origArr.findIndex(
            ({ asOfDate }) => asOfDate === dateToMatch
        );
        return index === firstMatchIndex;
    });

export const benchmarkBalanceFor = (date, benchMarkData) =>
    benchMarkData.map((benchmark) => {
        const matchedElement = benchmark.growthAmounts.find(
            ({ asOfDate }) => asOfDate === date
        );
        return matchedElement ? matchedElement.balance : 0;
    });

export const performanceChartCardUtil = (gqlResult, filter = []) => {
    if (!gqlResult) return null;

    let colors = comparisonColors;

    const fund = gqlResult.exchangeTradedFund || gqlResult.mutualFund;

    const { shortName, inceptionDate, benchmarks, ytdMonthlyAsOfDate } = fund;

    const contributionGrowth = fund.contributionGrowth || {};
    const { effectiveDate } = contributionGrowth;

    const benchmarkContributionGrowths =
        contributionGrowth.benchmarkContributionGrowths || [];
    const fundGrowthAmounts = contributionGrowth.fundGrowthAmounts || [];

    const canShow = calculateCanShow(inceptionDate, fundGrowthAmounts);

    let { name } = gqlResult.exchangeTradedFund || gqlResult.mutualFund;
    name = shortName || name;

    const getFilteredBenchmarkContributionGrowths = (
        benchmarksData,
        benchmarkContributionGrowthsData
    ) => {
        const primaryBenchmark = benchmarksData.find(
            (benchmark) => benchmark.benchmarkType === 'Primary'
        );

        return benchmarkContributionGrowthsData.filter(
            (benchmark) =>
                benchmark.benchmarkSymbol === primaryBenchmark.benchmarkCode
        );
    };
    const benchmarkContributionGrowthData = benchmarks
        ? getFilteredBenchmarkContributionGrowths(
              benchmarks,
              benchmarkContributionGrowths
          )
        : benchmarkContributionGrowths;

    const benchMarkData = benchmarkContributionGrowthData.map((ele) => {
        const { growthAmounts, benchmarkName } = ele; // || {};
        // account for duplicated dates from growthAmounts
        // use original data object to instatiate new set by dates
        // rebuild through mapping to find matching balance from original object to new object by date
        // FYI - utilizes first balance value for date found if dates are duplicated
        const dedupedGrowthAmounts = removeElementsWithDuplicateDates(
            growthAmounts
        );

        return { benchmarkName, growthAmounts: dedupedGrowthAmounts };
    });

    // accounts for duplicated dates in fundGrowthAmount
    const dedupedFundGrowthAmounts = removeElementsWithDuplicateDates(
        fundGrowthAmounts
    );

    const benchmarkNames = benchMarkData.map(
        ({ benchmarkName }) => benchmarkName
    );

    let data = dedupedFundGrowthAmounts.map((ele) => ({
        label: displayShortDate(ele.asOfDate),
        headers: [name, ...benchmarkNames],
        data: [ele.balance, ...benchmarkBalanceFor(ele.asOfDate, benchMarkData)]
    }));

    const legendData =
        (data &&
            data.length > 0 &&
            data[0].headers.map((ele, i) => {
                const totalReturn = data[data.length - 1].data[i];
                return {
                    label: ele,
                    value: `${displayCurrency(totalReturn)} (${
                        totalReturn > 10000 ? `+` : `-`
                    }${displayCurrency(totalReturn - 10000)})`,
                    color: colors[i],
                    checked: true
                };
            })) ||
        null;

    const asOf = displayShortDate(effectiveDate);
    if (filter.length > 0) {
        const indexToRemove = [];
        data = data.map((ele) => {
            ele.headers.forEach((header, index) => {
                if (filter.includes(header)) indexToRemove.push(index);
            });
            return {
                label: ele.label,
                headers: ele.headers.filter(
                    (header) => !filter.includes(header)
                ),
                data: ele.data.filter(
                    (_value, index) => !indexToRemove.includes(index)
                )
            };
        });
        colors = colors.filter(
            (_color, index) => !indexToRemove.includes(index)
        );
    }

    const { benchmarkName: displayedBenchmarkName } = benchMarkData[0] || {};

    return {
        asOf,
        colors,
        data,
        inceptionDate,
        legendData,
        ytdMonthlyAsOfDate: displayShortDateShortYear(ytdMonthlyAsOfDate),
        // only the primary benchmark is being used at the moment
        // so taking the first benchmark in a list of one essentially
        benchmarkName: displayedBenchmarkName,
        canShow
    };
};
